/**
 * The document snapshot sent to the agent with each message.
 *
 * The agent addresses the document by paragraph index (0-based position in
 * `body.paragraphs`), so every heading and range carries one. The snapshot
 * is deliberately partial: the outline, the selection and the region around
 * it (the enclosing heading to the next heading of the same or higher level,
 * or a window of paragraphs when the document has no headings). The agent
 * asks for more through a tool when it needs it.
 *
 * Mirrors `WordDocumentContextMiddleware` on the backend; keep the shapes in
 * step.
 */
import { type TemplateStyleMap, type TemplateStyleRef, loadAppliedTemplate } from "./identity";
import { setTemplateStyleMap } from "./revisionOoxml";
import { TEMPLATE_FRAME_TAG } from "./templateFrame";
import { readTables } from "./tables";

export type OutlineEntry = { index: number; level: number; text: string };

export type DocumentRange = { start: number; end: number; text: string };

/** One question's answer block: its paragraph range, or null when it isn't in the document. */
export type QuestionRange = { section_id: string; title: string; start: number | null; end: number | null };

/** A top-level table: the paragraph span its cells occupy, its size and first row. */
export type TableSummary = { index: number; end_index: number; rows: number; columns: number; first_row: string[] };

export type DocumentSnapshot = {
  title: string;
  paragraph_count: number;
  word_count: number;
  outline: OutlineEntry[];
  selection: DocumentRange | null;
  region: (DocumentRange & { heading: string | null }) | null;
  /** Answer blocks found in the document; the pane adds the bid's other questions. */
  questions: QuestionRange[];
  /** Tables, each once, so the model treats one as a unit rather than as empty cell paragraphs. */
  tables: TableSummary[];
  /** The template applied to this document and the styles its body uses, for the model's placement and style choices. */
  applied_template: { name: string; style_map: TemplateStyleMap | null } | null;
  /** The template's frame pages (cover/contents at the front, closing at the back) as paragraph spans. */
  template_frames: { kind: "front" | "back"; start: number; end: number }[];
  /** The styles the document's own text is written in, counted outside answer blocks (see {@link stylesInUse}). */
  styles_in_use: StylesInUse | null;
  capabilities: DocumentCapabilities;
};

export type DocumentCapabilities = {
  /** Edits can land as revisions (WordApi 1.4: change tracking is controllable). */
  tracked_changes: boolean;
  comments: boolean;
  /** Tracked changes can be listed, accepted and rejected from the pane (WordApi 1.6). */
  review: boolean;
  /** Document styles can be edited (WordApi 1.5). */
  styles: boolean;
};

/** A style as the document uses it: Word's id (what `w:pStyle` takes), its name, and how often. */
export type StyleUse = TemplateStyleRef & { count: number };

export type StylesInUse = {
  body: StyleUse;
  bullet: (StyleUse & { has_numbering: boolean }) | null;
  number: (StyleUse & { has_numbering: boolean }) | null;
  headings: Record<string, StyleUse>;
  table: StyleUse | null;
};

/**
 * Word's style id for a style name: built-in names are lower case in the
 * file ("heading 1" -> Heading1), custom ones keep their case with the
 * spaces removed ("ARO Response" -> AROResponse).
 */
export const styleIdOf = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join("")
    .replace(/[^A-Za-z0-9]+/g, "");

/**
 * Which styles the document's own text is written in: the paragraph style
 * most body text uses, the style of its list items, the heading style at
 * each level and the table style, counted outside answer blocks and tables
 * (a pull written in Normal must not teach the next pull to use Normal).
 * Null when the document has no text to learn from.
 */
export const stylesInUse = (
  paragraphs: { text: string; style: string; builtIn: string; list: boolean; inTable: boolean; inAnswer: boolean }[],
  tableStyles: string[]
): StylesInUse | null => {
  const body = new Map<string, number>();
  const list = new Map<string, number>();
  const headings = new Map<number, Map<string, number>>();
  const bump = (m: Map<string, number>, k: string) => m.set(k, (m.get(k) ?? 0) + 1);
  for (const p of paragraphs) {
    if (!p.text || p.inTable || p.inAnswer || !p.style) continue;
    const level = headingLevel(p.style, p.builtIn);
    if (level) {
      if (!headings.has(level)) headings.set(level, new Map());
      bump(headings.get(level) as Map<string, number>, p.style);
    } else if (p.list) bump(list, p.style);
    else if (!/^(toc|table of contents|caption|header|footer|title)/i.test(p.style)) bump(body, p.style);
  }
  const top = (m: Map<string, number>): [string, number] | null =>
    m.size ? [...m.entries()].sort((a, b) => b[1] - a[1])[0] : null;
  const bodyTop = top(body);
  if (!bodyTop) return null;
  const use = (hit: [string, number] | null): StyleUse | null =>
    hit ? { id: styleIdOf(hit[0]), name: hit[0], count: hit[1] } : null;
  const listTop = use(top(list));
  const heads: Record<string, StyleUse> = {};
  for (const [level, m] of headings) {
    const h = use(top(m));
    if (h) heads[String(level)] = h;
  }
  const tableTop = use(
    top(
      tableStyles
        .filter((s) => s && !/^(table normal|normal table)$/i.test(s))
        .reduce((m, s) => (bump(m, s), m), new Map<string, number>())
    )
  );
  return {
    body: use(bodyTop) as StyleUse,
    // A list item's style carries its numbering when it is not Word's
    // generic List Paragraph (whose bullets come from direct formatting).
    bullet: listTop ? { ...listTop, has_numbering: !/^list paragraph$/i.test(listTop.name) } : null,
    number: null,
    headings: heads,
    table: tableTop,
  };
};

/** Paragraphs sent on either side of the selection when there are no headings. */
const REGION_WINDOW = 12;
/** Cap on region text; the backend clips again, this saves the round trip. */
const REGION_MAX_CHARS = 12_000;
const SELECTION_MAX_CHARS = 4_000;

const HEADING_LEVEL = /heading\s*([1-9])/i;

export const isOfficeDocumentAvailable = (): boolean => {
  try {
    return typeof Word !== "undefined" && Boolean(Office?.context?.document);
  } catch {
    return false;
  }
};

/**
 * Which Word API the host supports; decides tracked changes and comments.
 * @returns The capability flags.
 */
export const documentCapabilities = (): DocumentCapabilities => {
  const supports = (set: string) => {
    try {
      return Office.context.requirements.isSetSupported("WordApi", set);
    } catch {
      return false;
    }
  };
  return {
    tracked_changes: supports("1.4"),
    comments: supports("1.4"),
    review: supports("1.6"),
    styles: supports("1.5"),
  };
};

const headingLevel = (style: string | null | undefined, builtIn: string | null | undefined): number | null => {
  for (const candidate of [builtIn, style]) {
    const match = candidate ? HEADING_LEVEL.exec(candidate) : null;
    if (match) return Number(match[1]);
  }
  if (builtIn && /^title$/i.test(builtIn)) return 1;
  return null;
};

const countWords = (text: string): number => (text.match(/\S+/g) ?? []).length;

const clip = (text: string, limit: number): string =>
  text.length <= limit ? text : `${text.slice(0, limit - 1).trimEnd()}…`;

type ParagraphInfo = { index: number; text: string; level: number | null; inTable?: boolean };

/**
 * The top-level tables as paragraph spans. Consecutive paragraphs inside a
 * table (nesting level above zero, nested tables included) are one span, and
 * spans pair with `body.tables` in document order; Word keeps a paragraph
 * between two tables, so spans never touch.
 * @param context - The batch; `paragraphs` must already carry `tableNestingLevel`.
 * @param paragraphs - The body's paragraphs, loaded.
 */
export const loadTableSpans = async (
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection
): Promise<TableSummary[]> => {
  const spans: { start: number; end: number }[] = [];
  paragraphs.items.forEach((p, i) => {
    if ((p.tableNestingLevel ?? 0) <= 0) return;
    const last = spans[spans.length - 1];
    if (last && last.end === i - 1) last.end = i;
    else spans.push({ start: i, end: i });
  });
  if (!spans.length) return [];
  const tables = await readTables(context, context.document.body.tables);
  return spans.map((span, k) => {
    const values = tables[k]?.values ?? [];
    return {
      index: span.start,
      end_index: span.end,
      rows: values.length,
      columns: values.reduce((n, row) => Math.max(n, row.length), 0),
      first_row: (values[0] ?? []).map((cell) => String(cell ?? "")),
    };
  });
};

/** One line standing in for a table in the text the model reads. */
export const tableLine = (table: TableSummary): string =>
  `[Table, paragraphs ${table.index}–${table.end_index}: ${table.rows} rows × ${table.columns} columns` +
  (table.first_row.some(Boolean) ? `; first row: ${table.first_row.join(" | ")}]` : "]");

/** Paragraph texts with each table span collapsed to one line. */
export const collapseTables = (infos: readonly ParagraphInfo[], tables: readonly TableSummary[]): string[] => {
  const starts = new Map(tables.map((t) => [t.index, t]));
  const out: string[] = [];
  let skipUntil = -1;
  infos.forEach((p) => {
    if (p.index <= skipUntil) return;
    const table = starts.get(p.index);
    if (table) {
      out.push(tableLine(table));
      skipUntil = table.end_index;
      return;
    }
    out.push(p.text);
  });
  return out;
};

/**
 * Locate the selection inside the paragraph list.
 *
 * Word gives the selected paragraphs back as a separate collection with no
 * index into the body, so the first and last are matched on their text. A
 * document with repeated paragraphs (e.g. several empty lines) is matched
 * nearest to the previous hit, which is right for any contiguous selection.
 * @param paragraphs - Every body paragraph.
 * @param selectedTexts - The texts of the selected paragraphs, in order.
 * @returns The index range, or null when the selection is not in the body
 *   (a header, footer or text box).
 */
export const locateSelection = (
  paragraphs: readonly { text: string }[],
  selectedTexts: readonly string[]
): { start: number; end: number } | null => {
  if (!selectedTexts.length) return null;
  const start = paragraphs.findIndex((p) => p.text === selectedTexts[0]);
  if (start === -1) return null;
  let end = start;
  for (let i = 1; i < selectedTexts.length; i += 1) {
    const next = paragraphs.findIndex((p, index) => index > end && p.text === selectedTexts[i]);
    if (next === -1) break;
    end = next;
  }
  return { start, end };
};

/**
 * The region around a position: from the nearest heading above to the next
 * heading of the same or higher level, or a window when there are none.
 * @param paragraphs - Every body paragraph with its heading level.
 * @param at - The paragraph to centre on (the selection start).
 * @returns The region bounds and its heading.
 */
export const regionAround = (
  paragraphs: readonly ParagraphInfo[],
  at: number
): { start: number; end: number; heading: string | null } => {
  const last = paragraphs.length - 1;
  const anchor = Math.max(0, Math.min(at, last));
  let headingIndex = -1;
  for (let i = anchor; i >= 0; i -= 1) {
    if (paragraphs[i].level !== null) {
      headingIndex = i;
      break;
    }
  }
  if (headingIndex === -1) {
    return {
      start: Math.max(0, anchor - REGION_WINDOW),
      end: Math.min(last, anchor + REGION_WINDOW),
      heading: null,
    };
  }
  const level = paragraphs[headingIndex].level as number;
  let end = last;
  for (let i = headingIndex + 1; i <= last; i += 1) {
    const other = paragraphs[i].level;
    if (other !== null && other <= level) {
      end = i - 1;
      break;
    }
  }
  return { start: headingIndex, end, heading: paragraphs[headingIndex].text };
};

/**
 * Read the document and build the snapshot.
 * @returns The snapshot, or null outside Office.
 */
export const readDocumentSnapshot = async (options: { questions?: boolean } = {}): Promise<DocumentSnapshot | null> => {
  if (!isOfficeDocumentAvailable()) return null;
  const applied = await loadAppliedTemplate().catch(() => null);
  return Word.run(async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("items/text,items/style,items/styleBuiltIn,items/tableNestingLevel,items/isListItem");
    const bodyTables = body.tables;
    bodyTables.load("items/style,items/styleBuiltIn");
    const selection = context.document.getSelection();
    // Navigation properties are proxies until sync; the loads below queue
    // against them, which the office-addins rule cannot see.
    // eslint-disable-next-line office-addins/call-sync-before-read, office-addins/load-object-before-read
    const selectedParagraphs = selection.paragraphs;
    selectedParagraphs.load("items/text");
    selection.load("text");
    const properties = context.document.properties;
    properties.load("title");
    const controls = body.contentControls;
    controls.load("items/tag");
    await context.sync();

    // The template's frame pages, by the paragraphs their controls hold, so
    // the model knows where the document's own content starts and ends.
    const frames: DocumentSnapshot["template_frames"] = [];
    if (controls.items.some((c) => c.tag === TEMPLATE_FRAME_TAG)) {
      const parents = paragraphs.items.map((p) => {
        const c = p.parentContentControlOrNullObject;
        c.load("isNullObject,tag");
        return c;
      });
      await context.sync();
      // Each run of consecutive paragraphs inside a frame control is one
      // frame: at the front when only empty paragraphs precede it, at the
      // back when only empty ones follow (Word keeps an empty paragraph
      // after a control that ends the body).
      const runs: { start: number; end: number }[] = [];
      let run: { start: number; end: number } | null = null;
      parents.forEach((c, index) => {
        const inFrame = !c.isNullObject && c.tag === TEMPLATE_FRAME_TAG;
        if (inFrame) run = run ? { start: run.start, end: index } : { start: index, end: index };
        else if (run) {
          runs.push(run);
          run = null;
        }
      });
      if (run) runs.push(run);
      const texts = paragraphs.items.map((p) => (p.text ?? "").trim());
      const blankBefore = (i: number) => texts.slice(0, i).every((t) => !t);
      const blankAfter = (i: number) => texts.slice(i + 1).every((t) => !t);
      runs.forEach((r, i) => {
        const kind: "front" | "back" = blankBefore(r.start)
          ? "front"
          : blankAfter(r.end)
            ? "back"
            : i === 0
              ? "front"
              : "back";
        frames.push({ kind, start: r.start, end: r.end });
      });
    }

    // Which paragraphs sit in a question's answer block (tag
    // mytender-q:{bid}:{section}); read only when the document has blocks.
    const questions: QuestionRange[] = [];
    // One object per paragraph: read only when asked (before a send), not on
    // every caret move.
    if (options.questions && controls.items.some((c) => (c.tag ?? "").startsWith("mytender-q:"))) {
      const parents = paragraphs.items.map((p) => {
        const c = p.parentContentControlOrNullObject;
        c.load("isNullObject,tag,title");
        return c;
      });
      await context.sync();
      const byId = new Map<string, QuestionRange>();
      parents.forEach((c, index) => {
        if (c.isNullObject) return;
        const parts = (c.tag ?? "").split(":");
        if (parts.length !== 3 || parts[0] !== "mytender-q") return;
        const found = byId.get(parts[2]);
        if (found) found.end = index;
        else byId.set(parts[2], { section_id: parts[2], title: c.title ?? "", start: index, end: index });
      });
      questions.push(...byId.values());
    }

    // The styles the document's text is in, outside answer blocks, so new
    // content can match a document that already looks like its template
    // even when no style map was recorded when the template was applied.
    const styleOwners = controls.items.some((c) => (c.tag ?? "").startsWith("mytender-q:"))
      ? paragraphs.items.map((p) => {
          const c = p.parentContentControlOrNullObject;
          c.load("isNullObject,tag");
          return c;
        })
      : null;
    if (styleOwners) await context.sync();
    const inUse = stylesInUse(
      paragraphs.items.map((p, index) => ({
        text: (p.text ?? "").trim(),
        style: p.style ?? "",
        builtIn: String(p.styleBuiltIn ?? ""),
        list: Boolean(p.isListItem),
        inTable: (p.tableNestingLevel ?? 0) > 0,
        inAnswer: Boolean(
          styleOwners && !styleOwners[index].isNullObject && (styleOwners[index].tag ?? "").startsWith("mytender-q:")
        ),
      })),
      bodyTables.items.map((t) => t.style ?? "")
    );
    if (!applied?.styleMap) setTemplateStyleMap(inUse ? { ...inUse, response_start: null } : null);

    const infos: ParagraphInfo[] = paragraphs.items.map((p, index) => ({
      index,
      text: p.text ?? "",
      level: headingLevel(p.style, String(p.styleBuiltIn ?? "")),
      inTable: (p.tableNestingLevel ?? 0) > 0,
    }));
    const tables = infos.some((p) => p.inTable) ? await loadTableSpans(context, paragraphs) : [];

    const outline: OutlineEntry[] = infos
      .filter((p) => p.level !== null && p.text.trim())
      .map((p) => ({ index: p.index, level: p.level as number, text: p.text.trim() }));

    const located = locateSelection(
      infos,
      selectedParagraphs.items.map((p) => p.text ?? "")
    );
    const selectedText = (selection.text ?? "").trim();
    const selectionRange: DocumentRange | null =
      located && selectedText ? { ...located, text: clip(selectedText, SELECTION_MAX_CHARS) } : null;

    const anchor = located?.start ?? 0;
    const bounds = infos.length ? regionAround(infos, anchor) : null;
    const region = bounds
      ? {
          ...bounds,
          text: clip(collapseTables(infos.slice(bounds.start, bounds.end + 1), tables).join("\n"), REGION_MAX_CHARS),
        }
      : null;

    const fullText = infos.map((p) => p.text).join("\n");
    const title = (properties.title || outline[0]?.text || "").trim();

    return {
      title,
      paragraph_count: infos.length,
      word_count: countWords(fullText),
      outline,
      selection: selectionRange,
      region,
      questions,
      tables,
      applied_template: applied ? { name: applied.name, style_map: applied.styleMap ?? null } : null,
      template_frames: frames,
      styles_in_use: inUse,
      capabilities: documentCapabilities(),
    };
  });
};
